import { useEffect, useRef, useState, useCallback } from 'react';
import { Application, Graphics, Container, Ticker } from 'pixi.js';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BALL_RADIUS,
  GRAVITY,
  JUMP_VELOCITY,
  COLORS,
  SCORE_INTERVAL_MS,
  STORAGE_KEY,
  GAME_STATES,
  UI_COLORS,
  type GameState,
} from '@/game/configs';
import { assets } from '@/game/assets';
import {
  startLooplayAttempt,
  trackPlayByType,
  trackMatch,
  emitGameEvent,
} from '@/looplay';
const getBestScore = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
};
const setBestScore = (score: number): void => {
  try {
    localStorage.setItem(STORAGE_KEY, score.toString());
  } catch {
    // Silent fail for storage errors
  }
};
export function HomePage() {
  const [gameState, setGameState] = useState<GameState>(GAME_STATES.LOBBY);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScoreState] = useState(getBestScore);
  const [isMuted, setIsMuted] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const ballContainerRef = useRef<Container | null>(null);
  const ballGraphicsRef = useRef<Graphics | null>(null);
  const ballYRef = useRef(0);
  const velocityYRef = useRef(0);
  const gameLoopRef = useRef<((delta: Ticker) => void) | null>(null);
  const scoreIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const matchIdRef = useRef<string>('');
  const audioContextRef = useRef<AudioContext | null>(null);
  const playJumpSound = useCallback(() => {
    if (isMuted) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.setValueAtTime(400, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.1);
    } catch {
      // Audio not available
    }
  }, [isMuted]);
  const playGameOverSound = useCallback(() => {
    if (isMuted) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.setValueAtTime(300, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch {
      // Audio not available
    }
  }, [isMuted]);
  const initializePixi = useCallback(async () => {
    if (!canvasRef.current || appRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    const app = new Application();
    await app.init({
      width: containerWidth,
      height: containerHeight,
      backgroundColor: COLORS.sky,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio, 2),
      autoDensity: true,
    });
    canvasRef.current.appendChild(app.canvas);
    appRef.current = app;
    const ballContainer = new Container();
    ballContainer.x = containerWidth / 2;
    ballContainer.y = containerHeight * 0.3;
    app.stage.addChild(ballContainer);
    const ball = new Graphics();
    ball.circle(0, 0, BALL_RADIUS);
    ball.fill({ color: COLORS.ball });
    ball.circle(-BALL_RADIUS * 0.3, -BALL_RADIUS * 0.3, BALL_RADIUS * 0.35);
    ball.fill({ color: COLORS.ballHighlight });
    ballContainer.addChild(ball);
    const ground = new Graphics();
    const groundHeight = 60;
    ground.rect(
      -containerWidth,
      containerHeight - groundHeight,
      containerWidth * 2,
      groundHeight
    );
    ground.fill({ color: COLORS.ground });
    ground.rect(
      -containerWidth,
      containerHeight - groundHeight,
      containerWidth * 2,
      8
    );
    ground.fill({ color: COLORS.groundDark });
    app.stage.addChild(ground);
    ballContainerRef.current = ballContainer;
    ballGraphicsRef.current = ball;
    ballYRef.current = containerHeight * 0.3;
    velocityYRef.current = 0;
  }, []);
  const cleanupPixi = useCallback(() => {
    if (appRef.current) {
      appRef.current.destroy(true, { children: true, texture: true });
      appRef.current = null;
      ballContainerRef.current = null;
      ballGraphicsRef.current = null;
    }
  }, []);
  const startGame = useCallback(() => {
    if (!canvasRef.current) return;
    const containerHeight = canvasRef.current.clientHeight;
    setScore(0);
    ballYRef.current = containerHeight * 0.3;
    velocityYRef.current = 0;
    startTimeRef.current = Date.now();
    matchIdRef.current = `match_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    startLooplayAttempt();
    scoreIntervalRef.current = window.setInterval(() => {
      setScore((prev) => prev + 1);
    }, SCORE_INTERVAL_MS);
    setGameState(GAME_STATES.PLAYING);
  }, []);
  const endGame = useCallback(() => {
    if (scoreIntervalRef.current) {
      clearInterval(scoreIntervalRef.current);
      scoreIntervalRef.current = null;
    }
    playGameOverSound();
    const finalScore = score;
    const currentBest = getBestScore();
    const isNewBest = finalScore > currentBest;
    if (isNewBest) {
      setBestScoreState(finalScore);
      setBestScore(finalScore);
    }
    const durationSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
    void trackPlayByType({
      trackingType: 'survival',
      startedAtMs: startTimeRef.current,
    });
    void trackMatch(matchIdRef.current, {
      durationSeconds,
      isCompleted: true,
      isWin: false,
    });
    void emitGameEvent('SCORE_SUBMITTED', {
      value: finalScore,
      refId: matchIdRef.current,
      payload: { isNewBest },
    });
    setGameState(GAME_STATES.GAME_OVER);
  }, [score, playGameOverSound]);
  const restartGame = useCallback(() => {
    startGame();
  }, [startGame]);
  const returnToLobby = useCallback(() => {
    setGameState(GAME_STATES.LOBBY);
  }, []);
  useEffect(() => {
    void initializePixi();
    return () => {
      cleanupPixi();
      if (scoreIntervalRef.current) {
        clearInterval(scoreIntervalRef.current);
      }
    };
  }, [initializePixi, cleanupPixi]);
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING || !appRef.current || !ballContainerRef.current) {
      if (gameLoopRef.current) {
        appRef.current?.ticker.remove(gameLoopRef.current);
        gameLoopRef.current = null;
      }
      return;
    }
    if (!canvasRef.current) return;
    const containerHeight = canvasRef.current.clientHeight;
    const groundY = containerHeight - 60;
    const gameLoop = (delta: Ticker) => {
      const dt = delta.deltaTime / 60;
      velocityYRef.current += GRAVITY * dt * 60;
      ballYRef.current += velocityYRef.current * dt * 60;
      const ballTop = ballYRef.current - BALL_RADIUS;
      if (ballTop < 0) {
        ballYRef.current = BALL_RADIUS;
        velocityYRef.current = Math.abs(velocityYRef.current) * 0.3;
      }
      if (ballYRef.current + BALL_RADIUS >= groundY) {
        ballYRef.current = groundY - BALL_RADIUS;
        velocityYRef.current = 0;
        endGame();
        return;
      }
      if (ballContainerRef.current) {
        ballContainerRef.current.y = ballYRef.current;
      }
    };
    gameLoopRef.current = gameLoop;
    appRef.current.ticker.add(gameLoop);
  }, [gameState, endGame]);
  const handleTap = useCallback(() => {
    if (gameState !== GAME_STATES.PLAYING) return;
    velocityYRef.current = JUMP_VELOCITY;
    playJumpSound();
  }, [gameState, playJumpSound]);
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      handleTap();
    },
    [handleTap]
  );
  return (
    <div className="relative h-[100dvh] w-screen overflow-hidden">
      <div
        ref={canvasRef}
        className="absolute inset-0 touch-none"
        onPointerDown={handlePointerDown}
      />
      <AnimatePresence mode="wait">
        {gameState === GAME_STATES.LOBBY && (
          <motion.div
            key="lobby"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex flex-col items-center justify-center p-6"
          >
            <div className="mb-12 w-full max-w-sm rounded-3xl p-8 shadow-[8px_8px_0px_rgba(139,115,85,0.25)]">
              <h1 className="mb-2 text-center text-4xl font-bold">
                Keep the Ball Up
              </h1>
              <p className="text-center text-lg">
                Tap to jump. Survive as long as you can!
              </p>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-4">
              <button
                onClick={() => {
                  void initializePixi().then(() => {
                    startGame();
                  });
                }}
                className="w-full rounded-2xl px-8 py-5 text-xl font-bold transition-transform active:scale-95"
                style={{
                  background: UI_COLORS.primary,
                  color: UI_COLORS.text,
                  boxShadow: `4px 4px 0px ${UI_COLORS.secondary}`,
                }}
              >
                Start Game
              </button>
              <button
                onClick={() => setGameState(GAME_STATES.HOW_TO_PLAY)}
                className="w-full rounded-2xl border-2 border-current px-8 py-5 text-xl font-bold transition-transform active:scale-95"
              >
                How to Play
              </button>
            </div>
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full transition-transform active:scale-90"
              style={{ background: UI_COLORS.surface, boxShadow: `2px 2px 0px ${UI_COLORS.shadowLight}` }}
            >
              <span className="text-xl">
                {isMuted ? '🔇' : '🔊'}
              </span>
            </button>
          </motion.div>
        )}
        {gameState === GAME_STATES.HOW_TO_PLAY && (
          <motion.div
            key="howtoplay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex flex-col items-center justify-center p-6"
          >
            <div className="mb-8 w-full max-w-sm rounded-3xl p-8 shadow-[8px_8px_0px_rgba(139,115,85,0.25)]">
              <h2 className="mb-6 text-center text-3xl font-bold">
                How to Play
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-lg font-bold text-stone-800">
                    1
                  </span>
                  <p>Tap or click anywhere to make the ball jump up</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-lg font-bold text-stone-800">
                    2
                  </span>
                  <p>The ball falls due to gravity — keep tapping to stay airborne</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-lg font-bold text-stone-800">
                    3
                  </span>
                  <p>Don&apos;t let the ball touch the ground! Game over if it falls</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-lg font-bold text-stone-800">
                    4
                  </span>
                  <p>Score increases every second you survive. Beat your best!</p>
                </div>
              </div>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-4">
              <button
                onClick={() => {
                  void initializePixi().then(() => {
                    startGame();
                  });
                }}
                className="w-full rounded-2xl px-8 py-5 text-xl font-bold transition-transform active:scale-95"
                style={{
                  background: UI_COLORS.primary,
                  color: UI_COLORS.text,
                  boxShadow: `4px 4px 0px ${UI_COLORS.secondary}`,
                }}
              >
                Start Game
              </button>
              <button
                onClick={returnToLobby}
                className="w-full rounded-2xl border-2 border-current px-8 py-5 text-xl font-bold transition-transform active:scale-95"
              >
                Back
              </button>
            </div>
          </motion.div>
        )}
        {gameState === GAME_STATES.PLAYING && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none absolute inset-0"
          >
            <div
              className="absolute left-4 right-4 top-4 flex items-center justify-between rounded-2xl px-4 py-2"
              style={{
                background: 'rgba(255, 248, 231, 0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: `2px 2px 0px ${UI_COLORS.shadowLight}`,
              }}
            >
              <div className="flex flex-col">
                <span className="text-xs font-medium">Score</span>
                <span className="text-2xl font-bold tabular-nums">
                  {score}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium">Best</span>
                <span className="text-2xl font-bold tabular-nums">
                  {bestScore}
                </span>
              </div>
            </div>
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="pointer-events-auto absolute right-4 top-20 flex h-10 w-10 items-center justify-center rounded-full transition-transform active:scale-90"
              style={{ background: UI_COLORS.surface, boxShadow: `2px 2px 0px ${UI_COLORS.shadowLight}` }}
            >
              <span className="text-lg">
                {isMuted ? '🔇' : '🔊'}
              </span>
            </button>
          </motion.div>
        )}
        {gameState === GAME_STATES.GAME_OVER && (
          <motion.div
            key="gameover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm rounded-3xl p-8 shadow-[8px_8px_0px_rgba(139,115,85,0.25)]"
            >
              <h2 className="mb-2 text-center text-3xl font-bold">
                Game Over
              </h2>
              <p className="mb-6 text-center text-lg">
                The ball fell!
              </p>
              <div className="mb-6 flex justify-around">
                <div className="text-center">
                  <span className="text-sm font-medium">Score</span>
                  <p className="text-4xl font-bold tabular-nums">
                    {score}
                  </p>
                </div>
                <div className="text-center">
                  <span className="text-sm font-medium">Best</span>
                  <p className="text-4xl font-bold tabular-nums">
                    {Math.max(score, bestScore)}
                  </p>
                </div>
              </div>
              {score >= bestScore && score > 0 && (
                <p className="mb-4 text-center text-lg font-bold">
                  🎉 New Best Score!
                </p>
              )}
              <div className="flex flex-col gap-3">
                <button
                  onClick={restartGame}
                  className="w-full rounded-2xl px-8 py-4 text-xl font-bold transition-transform active:scale-95"
                  style={{
                    background: UI_COLORS.primary,
                    color: UI_COLORS.text,
                    boxShadow: `4px 4px 0px ${UI_COLORS.secondary}`,
                  }}
                >
                  Play Again
                </button>
                <button
                  onClick={returnToLobby}
                  className="w-full rounded-2xl border-2 border-current px-8 py-4 text-xl font-bold transition-transform active:scale-95"
                >
                  Back to Lobby
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}